import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare, Send, Trash2, Pencil, Check, X as XIcon, Loader2,
  Reply as ReplyIcon, ArrowUp, Pin
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import KarmaBadge from "@/components/KarmaBadge";
import { useSmilies, SmilyPicker } from "@/components/smilies";
import MentionTextarea from "@/components/MentionTextarea";
import CommentText from "@/components/CommentText";
import ReactionBar from "@/components/ReactionBar";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

function Avatar({ name, picture, size = 36 }) {
  if (picture) {
    return (
      <img src={picture} alt="" className="rounded-full object-cover shrink-0"
           style={{ width: size, height: size }} />
    );
  }
  return (
    <div
      className="rounded-full bg-gradient-to-br from-[var(--tt-primary)] to-[var(--tt-purple)] flex items-center justify-center font-bold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {(name || "?")[0].toUpperCase()}
    </div>
  );
}

function CommentItem({ c, depth, isTop, currentUser, onUpdate, onDelete, onUpvote, onReply, onReactionChange, replying, replyText, setReplyText, postReply, postingReply, cancelReply, editingId, setEditingId, editText, setEditText, saveEdit, smilieMap, replyTextareaRef, editTextareaRef }) {
  const isOwn = currentUser && c.user_id === currentUser.user_id;
  const canDelete = isOwn || (currentUser && currentUser.role === "admin");
  const isEditing = editingId === c.comment_id;
  const canUpvote = currentUser && !isOwn;
  const isReply = depth > 0;

  return (
    <li
      data-testid={`comment-${c.comment_id}`}
      className={`flex gap-3 items-start animate-fade-up ${isReply ? "ml-10 pl-3 border-l-2 border-[var(--tt-border)]" : ""}`}
    >
      <Avatar name={c.user_name} picture={c.user_picture} size={isReply ? 28 : 36} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <Link
            to={`/u/${c.user_id}`}
            className="font-semibold text-sm hover:text-[var(--tt-primary)] transition-colors"
            data-testid={`comment-user-link-${c.comment_id}`}
          >
            {c.user_name}
          </Link>
          <KarmaBadge karma={c.author_karma || 0} />
          {isTop && (
            <span
              data-testid={`comment-top-badge-${c.comment_id}`}
              className="flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold bg-[var(--tt-yellow)] text-black px-1.5 py-0.5 rounded"
            >
              <Pin className="w-2.5 h-2.5" />
              Top
            </span>
          )}
          <span className="text-[10px] text-[var(--tt-text-faint)] font-mono-tt">
            {timeAgo(c.created_at)}{c.edited_at ? " · edited" : ""}
          </span>
        </div>

        {isEditing ? (
          <div className="mt-2">
            <textarea
              ref={editTextareaRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={2}
              className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
            />
            <div className="flex items-center gap-2 mt-2">
              <SmilyPicker
                size="sm"
                testIdPrefix={`comment-edit-${c.comment_id}`}
                inputRef={editTextareaRef}
                onInsert={(snip) => setEditText((t) => t + snip)}
              />
              <button onClick={() => saveEdit(c.comment_id)}
                data-testid={`comment-save-${c.comment_id}`}
                className="bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-[11px] font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                <Check className="w-3 h-3" /> Save
              </button>
              <button onClick={() => { setEditingId(null); setEditText(""); }}
                className="bg-[var(--tt-surface-2)] hover:bg-[#222] text-[11px] font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                <XIcon className="w-3 h-3" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-zinc-200 mt-1 whitespace-pre-wrap break-words">
            <CommentText text={c.text} mentions={c.mentions} smilieMap={smilieMap} />
          </p>
        )}

        {!isEditing && (
          <div className="flex items-center gap-3 mt-2 text-[11px] flex-wrap">
            <button
              onClick={() => canUpvote && onUpvote(c.comment_id)}
              disabled={!canUpvote}
              data-testid={`comment-upvote-${c.comment_id}`}
              title={!currentUser ? "Login to upvote" : isOwn ? "Cannot upvote your own" : c.upvoted_by_me ? "Remove upvote" : "Upvote"}
              className={`flex items-center gap-1 rounded-full px-2 py-0.5 transition-colors
                ${c.upvoted_by_me
                  ? "bg-[var(--tt-primary)]/15 text-[var(--tt-primary)] font-bold"
                  : "text-[var(--tt-text-muted)] hover:text-white"}
                ${!canUpvote ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
            >
              <ArrowUp className={`w-3 h-3 ${c.upvoted_by_me ? "fill-current" : ""}`} />
              <span className="font-mono-tt">{c.upvotes || 0}</span>
            </button>

            <ReactionBar
              commentId={c.comment_id}
              isOwn={isOwn}
              reactions={c.reactions || {}}
              myReactions={c.my_reactions || []}
              onChange={(patch) => onReactionChange?.(c.comment_id, patch)}
            />

            {!isReply && currentUser && (
              <button
                onClick={() => onReply(c.comment_id)}
                data-testid={`comment-reply-${c.comment_id}`}
                className="text-[var(--tt-text-muted)] hover:text-white flex items-center gap-1"
              >
                <ReplyIcon className="w-3 h-3" /> Reply
              </button>
            )}

            {isOwn && (
              <button onClick={() => { setEditingId(c.comment_id); setEditText(c.text); }}
                data-testid={`comment-edit-${c.comment_id}`}
                className="text-[var(--tt-text-muted)] hover:text-white flex items-center gap-1">
                <Pencil className="w-3 h-3" /> Edit
              </button>
            )}
            {canDelete && (
              <button onClick={() => onDelete(c.comment_id)}
                data-testid={`comment-delete-${c.comment_id}`}
                className="text-red-400 hover:text-red-300 flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
          </div>
        )}

        {/* Reply composer */}
        {replying === c.comment_id && (
          <div className="mt-3 flex gap-2 items-start">
            <Avatar name={currentUser?.name || currentUser?.email} picture={currentUser?.picture} size={26} />
            <div className="flex-1">
              <MentionTextarea
                ref={replyTextareaRef}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={`Reply to ${c.user_name}...`}
                rows={2}
                maxLength={2000}
                data-testid={`comment-reply-textarea-${c.comment_id}`}
                className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)] resize-y"
              />
              <div className="flex items-center gap-2 mt-1">
                <SmilyPicker
                  size="sm"
                  testIdPrefix={`comment-reply-${c.comment_id}`}
                  inputRef={replyTextareaRef}
                  onInsert={(snip) => setReplyText((t) => t + snip)}
                />
                <button
                  onClick={() => postReply(c.comment_id)}
                  disabled={postingReply || !replyText.trim()}
                  data-testid={`comment-reply-submit-${c.comment_id}`}
                  className="bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-[11px] font-semibold px-3 py-1 rounded-full flex items-center gap-1 disabled:opacity-50"
                >
                  {postingReply ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  Post reply
                </button>
                <button
                  onClick={cancelReply}
                  className="text-[11px] text-[var(--tt-text-muted)] hover:text-white"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}

export default function Comments({ tmdbId, mediaType }) {
  const { user } = useAuth();
  const { smilieMap } = useSmilies();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [replying, setReplying] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [postingReply, setPostingReply] = useState(false);
  const textareaRef = useRef(null);
  const replyTextareaRef = useRef(null);
  const editTextareaRef = useRef(null);

  const load = async () => {
    if (!tmdbId || !mediaType) return;
    setLoading(true);
    try {
      const { data } = await api.get("/comments", { params: { tmdb_id: tmdbId, media_type: mediaType } });
      setComments(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tmdbId, mediaType, user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/comments", {
        tmdb_id: tmdbId, media_type: mediaType, text: text.trim(),
      });
      setComments((prev) => [data, ...prev]);
      setText("");
      toast.success("Comment posted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to post");
    } finally {
      setBusy(false);
    }
  };

  const postReply = async (parentId) => {
    if (!replyText.trim()) return;
    setPostingReply(true);
    try {
      const { data } = await api.post("/comments", {
        tmdb_id: tmdbId, media_type: mediaType,
        text: replyText.trim(), parent_id: parentId,
      });
      setComments((prev) => [...prev, data]);
      setReplyText(""); setReplying(null);
      toast.success("Reply posted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to reply");
    } finally {
      setPostingReply(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${id}`);
      setComments((prev) => prev.filter((c) => c.comment_id !== id && c.parent_id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    try {
      const { data } = await api.patch(`/comments/${id}`, { text: editText.trim() });
      setComments((prev) => prev.map((c) => (c.comment_id === id ? data : c)));
      setEditingId(null); setEditText("");
      toast.success("Updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const upvote = async (id) => {
    try {
      const { data } = await api.post(`/comments/${id}/upvote`);
      setComments((prev) =>
        prev.map((c) => (c.comment_id === id
          ? { ...c, upvotes: data.upvotes, upvoted_by_me: data.upvoted_by_me }
          : c)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upvote failed");
    }
  };

  const reactionChange = (id, patch) => {
    setComments((prev) =>
      prev.map((c) => (c.comment_id === id ? { ...c, ...patch } : c)));
  };

  // Organise: top-level sorted by upvotes desc then created_at desc; pin highest.
  const { ordered, topId } = useMemo(() => {
    const tops = comments.filter((c) => !c.parent_id).slice();
    tops.sort((a, b) => {
      if ((b.upvotes || 0) !== (a.upvotes || 0)) return (b.upvotes || 0) - (a.upvotes || 0);
      return new Date(b.created_at) - new Date(a.created_at);
    });
    const childrenByParent = {};
    for (const c of comments.filter((c) => c.parent_id)) {
      (childrenByParent[c.parent_id] ||= []).push(c);
    }
    for (const k in childrenByParent) {
      childrenByParent[k].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
    const flat = [];
    for (const t of tops) {
      flat.push({ comment: t, depth: 0 });
      for (const child of (childrenByParent[t.comment_id] || [])) {
        flat.push({ comment: child, depth: 1 });
      }
    }
    const top = tops.length > 0 && (tops[0].upvotes || 0) > 0 ? tops[0].comment_id : null;
    return { ordered: flat, topId: top };
  }, [comments]);

  const cancelReply = () => { setReplying(null); setReplyText(""); };

  return (
    <section className="px-6 md:px-8 py-6 border-t border-[var(--tt-border)]" data-testid="comments-section">
      <h3 className="font-display text-xl font-bold flex items-center gap-2 mb-4">
        <MessageSquare className="w-5 h-5 text-[var(--tt-primary)]" />
        Discussion
        <span className="bg-[var(--tt-surface-2)] text-[11px] font-mono-tt px-2 py-0.5 rounded">
          {comments.length}
        </span>
      </h3>

      {user ? (
        <form onSubmit={submit} className="mb-6">
          <div className="flex gap-3 items-start">
            <Avatar name={user.name || user.email} picture={user.picture} />
            <div className="flex-1">
              <MentionTextarea
                ref={textareaRef}
                data-testid="comment-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Share your thoughts... type @ to tag a user"
                rows={2}
                maxLength={2000}
                className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)] resize-y"
              />
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-2">
                  <SmilyPicker
                    size="sm"
                    testIdPrefix="comment-new"
                    inputRef={textareaRef}
                    onInsert={(snip) => setText((t) => t + snip)}
                  />
                  <span className="text-[10px] text-[var(--tt-text-faint)] font-mono-tt">
                    {text.length}/2000
                  </span>
                </div>
                <button
                  type="submit"
                  data-testid="comment-submit-btn"
                  disabled={busy || !text.trim()}
                  className="bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-xs font-semibold px-4 py-1.5 rounded-full flex items-center gap-2 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Post
                </button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="mb-6 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg p-4 text-sm text-[var(--tt-text-muted)]">
          <Link to="/login" className="text-[var(--tt-primary)] font-semibold hover:underline" data-testid="comments-login-link">
            Login
          </Link>{" "}or{" "}
          <Link to="/register" className="text-[var(--tt-primary)] font-semibold hover:underline">
            create an account
          </Link>{" "}to join the discussion.
        </div>
      )}

      {loading ? (
        <div className="text-[var(--tt-text-muted)] text-sm">Loading comments...</div>
      ) : ordered.length === 0 ? (
        <div className="text-center text-[var(--tt-text-muted)] py-8 text-sm">
          No comments yet. Be the first to start the discussion.
        </div>
      ) : (
        <ul className="space-y-4" data-testid="comments-list">
          {ordered.map(({ comment, depth }) => (
            <CommentItem
              key={comment.comment_id}
              c={comment}
              depth={depth}
              isTop={comment.comment_id === topId}
              currentUser={user}
              onUpvote={upvote}
              onDelete={remove}
              onReply={(id) => { setReplying(id); setReplyText(""); }}
              onReactionChange={reactionChange}
              replying={replying}
              replyText={replyText}
              setReplyText={setReplyText}
              postReply={postReply}
              postingReply={postingReply}
              cancelReply={cancelReply}
              editingId={editingId}
              setEditingId={setEditingId}
              editText={editText}
              setEditText={setEditText}
              saveEdit={saveEdit}
              smilieMap={smilieMap}
              replyTextareaRef={replyTextareaRef}
              editTextareaRef={editTextareaRef}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
